import { ConfirmModal } from "./ConfirmModal";
import { useEffect, useState } from "react";
import type { PromptTemplate, PromptTemplateScope } from "../types";

interface Props {
  onClose: () => void;
  onUse: (body: string) => void;
  canCreateOrgScope: boolean;
}

interface FormState {
  id: string | null;
  title: string;
  body: string;
  scope: PromptTemplateScope;
}

const EMPTY_FORM: FormState = { id: null, title: "", body: "", scope: "personal" };

export function PromptTemplatesModal({ onClose, onUse, canCreateOrgScope }: Props) {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    fetch("/api/prompt-templates")
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((j: { items: PromptTemplate[] }) => setTemplates(j.items ?? []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !form) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, form]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? templates.filter((t) => t.title.toLowerCase().includes(q) || t.body.toLowerCase().includes(q))
    : templates;
  const personal = filtered.filter((t) => t.scope === "personal");
  const org = filtered.filter((t) => t.scope === "org");

  const save = async () => {
    if (!form) return;
    const title = form.title.trim();
    const body = form.body.trim();
    if (!title || !body) {
      setError("título e texto do prompt são obrigatórios");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const url = form.id ? `/api/prompt-templates/${encodeURIComponent(form.id)}` : "/api/prompt-templates";
      const r = await fetch(url, {
        method: form.id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, body, scope: form.scope }),
      });
      const j = await r.json().catch(() => ({}) as { error?: string });
      if (!r.ok) {
        setError(j.error ?? "falha ao salvar o prompt");
        return;
      }
      setForm(null);
      load();
    } catch {
      setError("falha ao salvar o prompt");
    } finally {
      setSaving(false);
    }
  };

  const [toDelete, setToDelete] = useState<PromptTemplate | null>(null);
  const remove = async (t: PromptTemplate) => {
    try {
      await fetch(`/api/prompt-templates/${encodeURIComponent(t.id)}`, { method: "DELETE" });
    } catch {
      /* ignore */
    }
    load();
  };

  const use = (t: PromptTemplate) => {
    onUse(t.body);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal prompt-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Prompts salvos"
      >
        <header className="modal-header">
          <h2>Prompts salvos</h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        {form ? (
          <div className="prompt-form">
            <input
              className="prompt-form-input"
              placeholder="Título do prompt"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              autoFocus
              spellCheck={false}
            />
            <textarea
              className="prompt-form-textarea"
              placeholder="Texto do prompt…"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={6}
            />
            {canCreateOrgScope && (
              <label className="prompt-form-scope">
                <input
                  type="checkbox"
                  checked={form.scope === "org"}
                  onChange={(e) => setForm({ ...form, scope: e.target.checked ? "org" : "personal" })}
                />
                Visível para toda a organização
              </label>
            )}
            {error && <p className="prompt-form-error">{error}</p>}
            <div className="prompt-form-actions">
              <button type="button" className="action-btn" onClick={() => setForm(null)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="action-btn active" onClick={() => void save()} disabled={saving}>
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="prompt-modal-toolbar">
              <input
                className="prompt-search"
                type="search"
                placeholder="Buscar prompts…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="button" className="action-btn active" onClick={() => setForm({ ...EMPTY_FORM })}>
                + Novo
              </button>
            </div>

            <div className="prompt-list">
              {loading ? (
                <p className="prompt-list-status">Carregando…</p>
              ) : filtered.length === 0 ? (
                <p className="prompt-list-status">
                  {search ? "Nada encontrado." : "Nenhum prompt salvo ainda - crie o primeiro."}
                </p>
              ) : (
                <>
                  {org.length > 0 && (
                    <>
                      <div className="prompt-list-group-label">Da organização</div>
                      {org.map((t) => (
                        <PromptRow key={t.id} t={t} onUse={() => use(t)} onEdit={() => setForm({ id: t.id, title: t.title, body: t.body, scope: t.scope })} onDelete={() => setToDelete(t)} />
                      ))}
                    </>
                  )}
                  {personal.length > 0 && (
                    <>
                      <div className="prompt-list-group-label">Pessoais</div>
                      {personal.map((t) => (
                        <PromptRow key={t.id} t={t} onUse={() => use(t)} onEdit={() => setForm({ id: t.id, title: t.title, body: t.body, scope: t.scope })} onDelete={() => setToDelete(t)} />
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
      {toDelete && (
        <ConfirmModal
          title="Apagar prompt"
          message={`Apagar o prompt "${toDelete.title}"?`}
          confirmLabel="Apagar"
          onConfirm={() => void remove(toDelete)}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  );
}

function PromptRow({
  t,
  onUse,
  onEdit,
  onDelete,
}: {
  t: PromptTemplate;
  onUse: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="prompt-row">
      <div className="prompt-row-main" onClick={onUse} role="button" tabIndex={0}>
        <span className="prompt-row-title">{t.title}</span>
        <span className="prompt-row-body">{t.body}</span>
      </div>
      <div className="prompt-row-actions">
        <button type="button" className="action-btn" onClick={onUse} title="Usar este prompt">
          Usar
        </button>
        {t.canEdit && (
          <>
            <button type="button" className="session-tool" onClick={onEdit} title="Editar">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 2.5l2.5 2.5M3 13l8.5-8.5 2.5 2.5L5.5 15.5 2.5 16l.5-3z" />
              </svg>
            </button>
            <button type="button" className="session-tool" onClick={onDelete} title="Apagar">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M3 4h10M6.5 4V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M5 4l.5 9a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1l.5-9" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
